/**
 * Shared logic for the two best-in-class audit surfaces:
 * `SectionAuditPanel` (the plain-Card variant) and `SectionAuditCard` (the rich
 * section-tinted dashboard tile). Both render distinct chrome but speak the same
 * data model and the same finding-row interaction, so the view-model type, the
 * severity maps, the score/freshness helpers, and the `<FindingRow>` itself live
 * here once and are imported by both. Each component keeps its own outer layout.
 */

import { useState, useTransition } from "react";
import { TriangleAlertIcon, WrenchIcon, XIcon } from "../../icons";
import { Button } from "../../button";
import { IntentBadge } from "../status-badge";
import type { Intent } from "../../tokens";

export type AuditFindingSeverity = "low" | "medium" | "high";
export type AuditFindingStatus = "open" | "fixing" | "resolved" | "dismissed";

export interface AuditFindingVm {
  id: number;
  severity: AuditFindingSeverity;
  title: string;
  detail: string | null;
  fix_prompt: string | null;
  status: AuditFindingStatus;
}

export const SEVERITY_INTENT: Record<AuditFindingSeverity, Intent> = {
  high: "danger",
  medium: "warning",
  low: "neutral",
};

export const SEVERITY_LABEL: Record<AuditFindingSeverity, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

export function scoreIntent(score: number): Intent {
  if (score >= 80) return "success";
  if (score >= 50) return "warning";
  return "danger";
}

/**
 * Freshness sentence under the score. The card opts into a "· due" suffix on the
 * never-audited case (it has room for it); the panel doesn't — hence `dueWhenNever`.
 */
export function freshnessLabel(
  ageDays: number | null,
  cadenceDays: number,
  isDue: boolean,
  dueWhenNever = false,
): string {
  if (ageDays == null) {
    return dueWhenNever && isDue ? "Never audited · due" : "Never audited";
  }
  const age =
    ageDays === 0 ? "today" : ageDays === 1 ? "1 day ago" : `${ageDays} days ago`;
  if (isDue) return `Last audited ${age} · due for re-audit`;
  return `Last audited ${age} · re-audits every ${cadenceDays} days`;
}

/**
 * Wraps an async run-audit action in a transition + error state. Shared by both
 * surfaces' "Run audit / Re-audit" buttons.
 */
export function useRunAudit(
  onRunAudit: () => Promise<void>,
  running: boolean,
): {
  busy: boolean;
  error: string | null;
  runAudit: () => void;
} {
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const busy = pending || running;

  function runAudit() {
    if (busy) return;
    setError(null);
    startTransition(async () => {
      try {
        await onRunAudit();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Audit failed to start");
      }
    });
  }

  return { busy, error, runAudit };
}

/**
 * One finding: severity badge + title (+ optional detail), then Dismiss / Fix.
 * `fixVariant` lets the rich card use its section-accented Fix button while the
 * plain panel keeps the default; `showDetail` lets the card omit the detail line.
 */
export function FindingRow({
  finding,
  onFix,
  onDismiss,
  fixVariant = "default",
  showDetail = true,
  gapClassName = "gap-2",
  actionsGapClassName = "gap-2",
}: {
  finding: AuditFindingVm;
  onFix: (findingId: number) => Promise<void>;
  onDismiss: (findingId: number) => Promise<void>;
  fixVariant?: "default" | "section";
  showDetail?: boolean;
  gapClassName?: string;
  actionsGapClassName?: string;
}) {
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const fixing = finding.status === "fixing";

  function act(fn: () => Promise<void>, label: string) {
    if (pending || fixing) return;
    setError(null);
    startTransition(async () => {
      try {
        await fn();
      } catch (err) {
        setError(err instanceof Error ? err.message : `${label} failed`);
      }
    });
  }

  return (
    <div className={`flex flex-col ${gapClassName}`} data-audit-finding={finding.id}>
      <div className="flex items-start gap-2">
        <IntentBadge
          intent={SEVERITY_INTENT[finding.severity]}
          withDot
          className="mt-0.5 shrink-0"
        >
          {SEVERITY_LABEL[finding.severity]}
        </IntentBadge>
        {showDetail ? (
          <div className="flex flex-col gap-0.5 min-w-0">
            <p className="type-body text-foreground">{finding.title}</p>
            {finding.detail ? (
              <p className="type-body text-muted-foreground">{finding.detail}</p>
            ) : null}
          </div>
        ) : (
          <p className="min-w-0 flex-1 type-body text-foreground">{finding.title}</p>
        )}
      </div>

      {error ? <p className="type-body text-destructive">{error}</p> : null}

      <div className={`flex items-center justify-end ${actionsGapClassName}`}>
        {fixing ? (
          <span
            className="flex items-center gap-1.5 type-body text-muted-foreground"
            title="A fix task was opened — it resolves when a different agent ships and verifies it through the gate."
          >
            <WrenchIcon aria-hidden="true" className="size-3.5 animate-pulse" />
            Fix queued
          </span>
        ) : (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => act(() => onDismiss(finding.id), "Dismiss")}
              disabled={pending}
              aria-label={`Dismiss finding: ${finding.title}`}
            >
              <XIcon aria-hidden="true" />
              Dismiss
            </Button>
            {finding.fix_prompt ? (
              <Button
                type="button"
                variant={fixVariant}
                size="sm"
                onClick={() => act(() => onFix(finding.id), "Fix")}
                disabled={pending}
                aria-label={`Fix finding with a coding agent: ${finding.title}`}
              >
                <WrenchIcon aria-hidden="true" />
                {pending ? "Starting…" : "Fix with agent"}
              </Button>
            ) : (
              <span className="flex items-center gap-1.5 type-body text-muted-foreground">
                <TriangleAlertIcon aria-hidden="true" className="size-3.5" />
                Needs a human
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
